"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import TeamInviteWaitingNotice from "@/src/features/team/client/TeamInviteWaitingNotice";
import type { WorkspaceAuthStatus } from "@/src/features/auth/client/WorkspaceAuthFlowRuntime";
import {
  clearLoaderContinuity,
  WORKSPACE_LOADER_CONTINUITY_KEY,
} from "@/src/features/loading/loadingContinuity";

type AuthStatus = WorkspaceAuthStatus;

interface WorkspaceAuthFlowValue extends Omit<AuthStatus, "retryInitialization"> {
  beginInitialization: () => void;
  retryInitialization: () => void;
  workspaceRevealReady: boolean;
  /** The runtime is painting the workspace loader; handoff loaders yield to it. */
  workspaceLoaderVisible: boolean;
}

const WorkspaceAuthFlowContext = createContext<WorkspaceAuthFlowValue | null>(null);

function loadWorkspaceAuthFlowRuntime() {
  return import("@/src/features/auth/client/WorkspaceAuthFlowRuntime");
}

// Rendered with SSR so a dashboard load streams the loader and preloads its
// chunk; public pages never render it, so they never download the auth stack.
// The null fallback keeps a pending chunk from suspending the page around it.
const WorkspaceAuthFlowRuntime = dynamic(
  () => import("@/src/features/auth/client/WorkspaceAuthFlowRuntime"),
  { loading: () => null },
);

async function signOutBeforeRuntime(): Promise<void> {}
function retryBeforeRuntime(): void {}
function resumeBeforeRuntime(): void {}

// Matches useAuthStatus's initial return. Context consumers only mount on
// routes that start the runtime, which replaces these in a layout effect
// before the first paint.
const IDLE_AUTH_STATUS: AuthStatus = {
  passageSignOut: signOutBeforeRuntime,
  initialLoadComplete: false,
  initializationProgress: 0,
  initializationStatus: "Preparing secure workspace",
  initializationRedirect: null,
  initializationError: null,
  initializationErrorTitle: null,
  initializationDetail: null,
  siloAccessGate: false,
  teamInviteWaiting: false,
  retryInitialization: retryBeforeRuntime,
  resumeInitialization: resumeBeforeRuntime,
};

function isDashboardPath(pathname: string): boolean {
  return pathname === "/dashboard" || pathname.startsWith("/dashboard/");
}

/**
 * Owns the protected-workspace bootstrap above both the WorkOS callback and
 * dashboard routes. The provider survives their client-side route replacement,
 * so the real auth progress animation mounts once instead of restarting at the
 * dashboard boundary.
 */
export default function WorkspaceAuthFlowProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const dashboardPath = isDashboardPath(pathname);
  const callbackPath = pathname === "/auth/callback";
  const [handoffRequested, setHandoffRequested] = useState(false);
  const initializationActive = dashboardPath || handoffRequested;
  const [runtimeStarted, setRuntimeStarted] = useState(false);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(IDLE_AUTH_STATUS);
  const [loaderAnimationComplete, setLoaderAnimationComplete] = useState(false);
  const [workspaceRevealReady, setWorkspaceRevealReady] = useState(false);
  const [workspaceLoaderVisible, setWorkspaceLoaderVisible] = useState(false);

  // The callback exchanges its code before starting the handoff; fetch the
  // runtime meanwhile so the loader appears the moment it is requested.
  useEffect(() => {
    if (callbackPath) void loadWorkspaceAuthFlowRuntime();
  }, [callbackPath]);

  // Once started, the runtime stays mounted so its auth state survives route
  // changes exactly as the always-mounted hook's did.
  useEffect(() => {
    if (initializationActive) setRuntimeStarted(true);
  }, [initializationActive]);

  const beginInitialization = useCallback(() => {
    setWorkspaceRevealReady(false);
    setLoaderAnimationComplete(false);
    setHandoffRequested(true);
  }, []);

  // A retry after an error starts the loader over, as the work does.
  const retryInitialization = useCallback(() => {
    clearLoaderContinuity(WORKSPACE_LOADER_CONTINUITY_KEY);
    setWorkspaceRevealReady(false);
    setLoaderAnimationComplete(false);
    authStatus.retryInitialization();
  }, [authStatus.retryInitialization]);

  useEffect(() => {
    if (dashboardPath || callbackPath) return;
    clearLoaderContinuity(WORKSPACE_LOADER_CONTINUITY_KEY);
    setHandoffRequested(false);
    setLoaderAnimationComplete(false);
    setWorkspaceRevealReady(false);
  }, [callbackPath, dashboardPath]);

  useEffect(() => {
    if (dashboardPath && loaderAnimationComplete && authStatus.initialLoadComplete) {
      setWorkspaceRevealReady(true);
    }
  }, [authStatus.initialLoadComplete, dashboardPath, loaderAnimationComplete]);

  const handleLoaderAnimationComplete = useCallback(() => {
    setLoaderAnimationComplete(true);
  }, []);

  const value: WorkspaceAuthFlowValue = {
    ...authStatus,
    beginInitialization,
    retryInitialization,
    workspaceRevealReady,
    workspaceLoaderVisible,
  };

  return (
    <WorkspaceAuthFlowContext.Provider value={value}>
      {children}
      {workspaceRevealReady && authStatus.teamInviteWaiting ? <TeamInviteWaitingNotice /> : null}
      {(initializationActive || runtimeStarted) && (
        <WorkspaceAuthFlowRuntime
          initializationActive={initializationActive}
          workspaceRevealReady={workspaceRevealReady}
          onAuthStatus={setAuthStatus}
          onLoaderAnimationComplete={handleLoaderAnimationComplete}
          onLoaderVisibleChange={setWorkspaceLoaderVisible}
        />
      )}
    </WorkspaceAuthFlowContext.Provider>
  );
}

export function useWorkspaceAuthFlow(): WorkspaceAuthFlowValue {
  const value = useContext(WorkspaceAuthFlowContext);
  if (!value) {
    throw new Error("useWorkspaceAuthFlow must be used within WorkspaceAuthFlowProvider");
  }
  return value;
}
